import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus } from 'lucide-react';
import { useRaces } from '../../hooks';
import { SearchableDropdown, tierDropdownOptions, divineStyles, ImageUpload } from './AddCharacterModal';

/**
 * Modal untuk mengedit karakter yang sudah ada
 */
const EditCharacterModal = ({ isOpen, onClose, onSubmit, onUploadImage, character, isDarkMode }) => {
    const { races, loading: racesLoading, addRace } = useRaces();

    const [form, setForm] = useState({
        name: '',
        race_id: '',
        title: '',
        tier: 'common',
        image: '',
    });

    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const [showAddRace, setShowAddRace] = useState(false);
    const [newRaceName, setNewRaceName] = useState('');
    const [addingRace, setAddingRace] = useState(false);

    // Populate form when character changes
    useEffect(() => {
        if (character) {
            setForm({
                name: character.name || '',
                race_id: character.race_id ? String(character.race_id) : '',
                title: character.title || '',
                tier: character.tier || 'common',
                image: character.image || '',
            });
            setError('');
        }
    }, [character]);

    if (!isOpen || !character) return null;

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleAddRace = async () => {
        if (!newRaceName.trim()) return;

        setAddingRace(true);
        const result = await addRace(newRaceName.trim());
        setAddingRace(false);

        if (result && !result.error) {
            setForm(prev => ({ ...prev, race_id: String(result.id) }));
            setNewRaceName('');
            setShowAddRace(false);
        } else {
            setError(result?.error || 'Failed to add race');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) return setError('Name is required');
        if (!form.race_id) return setError('Race is required');
        if (!form.title.trim()) return setError('Title is required');

        setSubmitting(true);
        setError('');

        const success = await onSubmit({
            id: parseInt(character.id, 10),
            name: form.name.trim(),
            race_id: parseInt(form.race_id, 10),
            title: form.title.trim(),
            tier: form.tier,
            image: form.image || null,
        });

        setSubmitting(false);

        if (success) {
            onClose();
        }
    };

    const inputClass = `
        w-full px-3 py-2 rounded-lg text-xs transition-colors outline-none
        ${isDarkMode
            ? 'bg-slate-700/50 border border-slate-600 text-slate-100 focus:border-indigo-500 placeholder-slate-500'
            : 'bg-slate-50 border border-slate-200 text-slate-800 focus:border-indigo-500 placeholder-slate-400'
        }
    `;

    const labelClass = `block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`;

    const raceOptions = races.map(r => ({ id: String(r.id), label: r.name }));

    return createPortal(
        <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <style>{divineStyles}</style>

            <div
                className={`
                    w-full max-w-md rounded-2xl shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto modal-scroll
                    ${isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-800'}
                `}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`
                    flex justify-between items-center px-5 py-3.5 border-b sticky top-0 z-10
                    ${isDarkMode ? 'border-slate-700/50 bg-slate-900' : 'border-slate-100 bg-white'}
                `}>
                    <h3 className="font-semibold text-xs">Edit Character</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`
                            p-1.5 rounded-full transition-colors cursor-pointer
                            ${isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}
                        `}
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-5 space-y-3.5">
                    {/* Image + Name (side by side) */}
                    <div>
                        <label className={labelClass}>Character</label>
                        <div className="flex gap-2.5 items-start">
                            <ImageUpload
                                imageUrl={form.image}
                                onImageChange={(url) => handleChange('image', url)}
                                onUpload={async (file) => {
                                    setUploading(true);
                                    try {
                                        const url = await onUploadImage(file);
                                        setUploading(false);
                                        return url;
                                    } catch (err) {
                                        setUploading(false);
                                        setError(err.message || 'Upload failed');
                                        throw err;
                                    }
                                }}
                                uploading={uploading}
                                isDarkMode={isDarkMode}
                            />
                            <div className="flex-1 space-y-1.5">
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="Name"
                                    className={inputClass}
                                    autoFocus
                                />
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="Title, e.g. Thunder Guardian"
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Race */}
                    <div>
                        <label className={labelClass}>Race</label>
                        {!showAddRace ? (
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <SearchableDropdown
                                        value={form.race_id}
                                        onChange={(id) => handleChange('race_id', id)}
                                        options={raceOptions}
                                        placeholder={racesLoading ? 'Loading...' : 'Select race'}
                                        searchPlaceholder="Search race..."
                                        noResultText="No race found"
                                        isDarkMode={isDarkMode}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowAddRace(true)}
                                    className={`
                                        px-2.5 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 shrink-0
                                        ${isDarkMode
                                            ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                            : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                                        }
                                    `}
                                    title="Add new race"
                                >
                                    <Plus size={12} />
                                    <span className="hidden sm:inline">New</span>
                                </button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newRaceName}
                                    onChange={(e) => setNewRaceName(e.target.value)}
                                    placeholder="New race name"
                                    className={`${inputClass} flex-1`}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddRace();
                                        }
                                        if (e.key === 'Escape') {
                                            setShowAddRace(false);
                                            setNewRaceName('');
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddRace}
                                    disabled={addingRace || !newRaceName.trim()}
                                    className="px-2.5 py-2 rounded-lg text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50 shrink-0"
                                >
                                    {addingRace ? '...' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowAddRace(false); setNewRaceName(''); }}
                                    className={`
                                        px-2 py-2 rounded-lg transition-colors shrink-0
                                        ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}
                                    `}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Tier */}
                    <div>
                        <label className={labelClass}>Tier</label>
                        <SearchableDropdown
                            value={form.tier}
                            onChange={(val) => handleChange('tier', val)}
                            options={tierDropdownOptions}
                            placeholder="Select tier"
                            searchPlaceholder="Search tier..."
                            noResultText="No tier found"
                            isDarkMode={isDarkMode}
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <p className="text-xs text-rose-500 bg-rose-500/10 px-3 py-2 rounded-lg">
                            {error}
                        </p>
                    )}

                    {/* Actions */}
                    <div className={`
                        flex gap-3 pt-2 border-t
                        ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}
                    `}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`
                                flex-1 px-4 py-2 rounded-lg text-xs font-medium transition-colors
                                ${isDarkMode
                                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                                }
                            `}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || uploading}
                            className="flex-1 px-4 py-2 rounded-lg text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50 shadow-sm"
                        >
                            {submitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default EditCharacterModal;
